<template>
  <div :class="containerClass">
    <div class="page-section">
      <!-- Loading the brief -->
      <div v-if="isLoading" class="text-center py-5">
        <i class="fas fa-circle-notch fa-spin text-muted" style="font-size: 30px;"></i>
      </div>

      <!-- Something is wrong with the link or the student's access -->
      <div v-else-if="loadError" class="card">
        <div class="card-body text-center py-5">
          <md-icon class="text-danger" style="font-size: 42px;">error_outline</md-icon>
          <h4 class="mt-3 mb-2">This interview isn't available</h4>
          <p class="text-muted mb-4">{{ loadError }}</p>
          <b-btn v-if="returnTo" variant="primary" @click="goBackToCourse">Back to my course</b-btn>
          <b-btn v-else variant="primary" :to="{ name: 'student-dashboard' }">Go to dashboard</b-btn>
        </div>
      </div>

      <template v-else>
        <!-- Where the student came from -->
        <div class="d-flex align-items-center mb-3 flex-wrap" style="gap: 8px;">
          <b-btn v-if="returnTo" size="sm" variant="light" @click="goBackToCourse">
            <md-icon style="font-size: 16px;">arrow_back</md-icon> Back to course
          </b-btn>
          <span class="text-muted small">
            {{ context.assignment.title }} &middot; {{ context.lesson.title }}
          </span>
          <b-badge v-if="context.assignment.is_graded" variant="warning">Graded</b-badge>
        </div>

        <!-- Post-call confirmation -->
        <div v-if="phase === 'completing' || phase === 'completed'" class="card">
          <div class="card-body text-center py-5">
            <template v-if="phase === 'completing'">
              <i class="fas fa-circle-notch fa-spin text-primary" style="font-size: 34px;"></i>
              <h4 class="mt-3 mb-2">Saving your interview</h4>
              <p class="text-muted mb-0">
                We're recording your responses against this assignment. This only takes a moment.
              </p>
            </template>

            <template v-else>
              <md-icon class="text-success" style="font-size: 46px;">check_circle</md-icon>
              <h4 class="mt-3 mb-2">Interview complete</h4>
              <p class="text-muted">
                <span v-if="pendingSiblings.length">
                  {{ completedCount }} of {{ context.assignment.total_tasks }} interviews done for this
                  assignment.
                </span>
                <span v-else>
                  You've finished every interview in this assignment.
                </span>
              </p>

              <!-- Remaining interviews, so the student doesn't hop back and forth -->
              <div v-if="pendingSiblings.length" class="mt-4 text-left mx-auto" style="max-width: 460px;">
                <h6 class="text-muted text-uppercase small mb-2">Still to do</h6>
                <div
                  v-for="sibling in pendingSiblings"
                  :key="sibling.id"
                  class="d-flex align-items-center justify-content-between border rounded p-2 mb-2"
                >
                  <span>{{ sibling.title }}</span>
                  <b-btn size="sm" variant="primary" @click="switchToTask(sibling)">Start</b-btn>
                </div>
              </div>

              <div class="mt-4">
                <b-btn v-if="returnTo" variant="primary" @click="goBackToCourse">Back to my course</b-btn>
                <b-btn v-else variant="primary" :to="{ name: 'student-dashboard' }">Go to dashboard</b-btn>
              </div>
            </template>
          </div>
        </div>

        <!-- The brief -->
        <div v-else class="card">
          <div class="card-body">
            <div class="d-flex align-items-start" style="gap: 20px;">
              <img
                v-if="context.simulation.image_url"
                :src="context.simulation.image_url"
                alt=""
                class="rounded"
                style="width: 96px; height: 96px; object-fit: cover;"
              />
              <div style="flex: 1; min-width: 0;">
                <h3 class="mb-1">{{ context.simulation.title }}</h3>
                <p class="text-muted mb-0">{{ context.task.title }}</p>
              </div>
            </div>

            <div v-if="context.simulation.instructions" class="mt-4">
              <h6 class="text-uppercase text-muted small mb-2">Before you begin</h6>
              <p class="mb-0" style="white-space: pre-line;">{{ context.simulation.instructions }}</p>
            </div>

            <div v-if="context.simulation.details" class="mt-4">
              <h6 class="text-uppercase text-muted small mb-2">Scenario</h6>
              <div v-html="context.simulation.details"></div>
            </div>

            <b-alert v-if="context.already_completed" show variant="success" class="mt-4 mb-0">
              You've already completed this interview in your current attempt.
            </b-alert>

            <b-alert v-else show variant="info" class="mt-4 mb-0">
              This is a spoken interview — you'll need a working microphone. Your task is marked complete
              automatically once the call ends.
            </b-alert>

            <div class="mt-4">
              <b-btn
                variant="primary"
                size="lg"
                :disabled="isStarting || context.already_completed"
                @click="beginInterview"
              >
                <i v-if="isStarting" class="fas fa-circle-notch fa-spin mr-1"></i>
                <md-icon v-else style="font-size: 18px;">mic</md-icon>
                Start interview
              </b-btn>
            </div>
          </div>
        </div>
      </template>
    </div>

    <component
      :is="interviewModal"
      v-if="context"
      :simulation="modalSimulation"
      :show-modal="showSimulationModal"
      :register-call="registerScenarioCall"
      @close="showSimulationModal = false"
      @call-ended="onCallEnded"
      @start-failed="onStartFailed"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { get } from 'lodash';
import Page from '@/components/Page.vue';
import SimulationModal from './SimulationModal.vue';
import AnamInterviewModal from './AnamInterviewModal.vue';

// Retell's webhook normally lands within a second or two. Anam's path is
// longer (browser ping -> Celery -> transcript), so we keep polling well past
// that; the credit is already safe server-side either way.
const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 30;

export default {
  name: 'ScenarioInterview',
  components: { SimulationModal, AnamInterviewModal },
  extends: Page,

  data() {
    return {
      context: null,
      isLoading: true,
      loadError: '',
      isStarting: false,
      showSimulationModal: false,
      phase: 'brief', // brief | completing | completed
      completedCount: 0,
      pendingSiblings: [],
      pollTimer: null,
    };
  },

  computed: {
    assignmentId() {
      return this.$route.query.assignment;
    },
    lessonId() {
      return this.$route.query.lesson;
    },
    taskId() {
      return this.$route.query.task;
    },
    returnTo() {
      return this.$route.query.returnTo || '';
    },
    // Anam streams a video avatar and needs its own SDK; Retell is audio-only.
    interviewModal() {
      return get(this.context, 'simulation.provider') === 'anam' ? 'AnamInterviewModal' : 'SimulationModal';
    },
    // SimulationModal renders the agent avatar from the simulation's voice.
    modalSimulation() {
      if (!this.context) return {};
      return {
        ...this.context.simulation,
        agent: {
          voice_id: this.context.simulation.voice_id,
          provider: this.context.simulation.provider,
        },
      };
    },
    breadcrumb() {
      return [{ text: 'Interview', active: true }];
    },
  },

  methods: {
    ...mapActions('assignments', [
      'getScenarioContext',
      'startScenarioInterview',
      'getScenarioTaskStatus',
    ]),
    get,

    async fetchContext() {
      this.isLoading = true;
      this.loadError = '';
      if (!this.assignmentId || !this.lessonId || !this.taskId) {
        this.loadError = 'This interview link is incomplete. Please open it again from your course.';
        this.isLoading = false;
        return;
      }
      try {
        const res = await this.getScenarioContext({
          assignment_id: this.assignmentId,
          lesson_id: this.lessonId,
          task_id: this.taskId,
        });
        this.context = res.data;
        this.completedCount = res.data.assignment.completed_tasks;
        this.pendingSiblings = (res.data.sibling_tasks || []).filter(t => t.status !== 'done');
      } catch (e) {
        this.loadError = get(e, 'response.data.message', "We couldn't load this interview.");
      }
      this.isLoading = false;
    },

    beginInterview() {
      this.isStarting = true;
      this.showSimulationModal = true;
    },

    // Handed to SimulationModal so the call is registered against this task
    // rather than through the generic simulation endpoint.
    async registerScenarioCall() {
      try {
        const res = await this.startScenarioInterview({
          assignment_id: this.assignmentId,
          lesson_id: this.lessonId,
          task_id: this.taskId,
        });
        return get(res, 'data', null);
      } catch (e) {
        this.makeToast({
          variant: 'danger',
          msg: get(e, 'response.data.message', "We couldn't start the interview."),
        });
        return null;
      } finally {
        this.isStarting = false;
      }
    },

    onStartFailed() {
      this.isStarting = false;
      this.showSimulationModal = false;
    },

    onCallEnded() {
      this.isStarting = false;
      this.phase = 'completing';
      this.pollForCompletion(0);
    },

    async pollForCompletion(attempt) {
      try {
        const res = await this.getScenarioTaskStatus({
          assignment_id: this.assignmentId,
          lesson_id: this.lessonId,
        });
        const tasks = get(res, 'data.tasks', []);
        const thisTask = tasks.find(t => String(t.id) === String(this.taskId));

        if (get(thisTask, 'status') === 'done') {
          this.completedCount = get(res, 'data.completed_tasks', this.completedCount);
          this.pendingSiblings = tasks.filter(
            t => String(t.id) !== String(this.taskId) && t.status !== 'done'
          );
          this.phase = 'completed';
          return;
        }
      } catch (e) {
        // Fall through to the retry / give-up path below.
      }

      if (attempt + 1 >= POLL_ATTEMPTS) {
        // The webhook is authoritative and will still land; don't leave the
        // student staring at a spinner over it.
        this.phase = 'completed';
        return;
      }
      this.pollTimer = setTimeout(() => this.pollForCompletion(attempt + 1), POLL_INTERVAL_MS);
    },

    switchToTask(sibling) {
      this.$router.replace({
        name: 'student-scenario-interview',
        query: { ...this.$route.query, task: sibling.id },
      });
    },

    goBackToCourse() {
      if (this.returnTo) {
        window.location.href = this.returnTo;
      }
    },
  },

  watch: {
    // Switching to a sibling interview re-runs the whole brief -> call flow.
    '$route.query.task'() {
      this.phase = 'brief';
      this.showSimulationModal = false;
      this.fetchContext();
    },
  },

  mounted() {
    this.fetchContext();
  },

  beforeDestroy() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },
};
</script>
